import React, { useState, useCallback, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';

interface RoutePoint {
  x: number;
  y: number;
  type: 'start' | 'waypoint' | 'end';
}

interface InteractiveWaypointsProps {
  pathPoints: RoutePoint[];
  isSelected: boolean;
  onWaypointUpdate: (waypoints: RoutePoint[]) => void;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  edgeType?: string;
}

const InteractiveWaypoints: React.FC<InteractiveWaypointsProps> = ({
  pathPoints,
  isSelected,
  onWaypointUpdate,
  sourceX,
  sourceY,
  targetX,
  targetY,
  edgeType
}) => {
  const { screenToFlowPosition } = useReactFlow();
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const dragRef = useRef<RoutePoint | null>(null);
  const pointerTargetRef = useRef<HTMLElement | SVGElement | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const pendingWaypointsRef = useRef<RoutePoint[] | null>(null);
  // const lastLogTsRef = useRef<number>(0);

  // Generate waypoints based on edge type and current path
  const generateWaypoints = useCallback((points: RoutePoint[], type?: string) => {
    const existing = points.filter(p => p.type === 'waypoint');
    if (existing.length >= 1) {
      return existing;
    } else {
      // Generate default waypoints based on edge type
      if (type === 'smoothstep') {
        // For smoothstep, use same algorithm as CustomEdge
        // Horizontal line goes above both points
        const highestY = Math.min(sourceY, targetY);
        const midY = highestY - 30; // 30px above the highest point
        
        // First waypoint: middle of vertical segment from source to midY
        const midVerticalX = sourceX;
        const midVerticalY = (sourceY + midY) / 2;
        
        // Second waypoint: middle of horizontal segment at midY
        const midHorizontalX = (sourceX + targetX) / 2;
        const midHorizontalY = midY;
        
        return [
          {
            x: midVerticalX,
            y: midVerticalY,
            type: 'waypoint' as const
          },
          {
            x: midHorizontalX,
            y: midHorizontalY,
            type: 'waypoint' as const
          }
        ];
      } else if (type === 'step') {
        // For step, use old algorithm (corner at target Y)
        const cornerX = sourceX;
        const cornerY = targetY;
        
        const midVerticalX = sourceX;
        const midVerticalY = (sourceY + cornerY) / 2;
        
        const midHorizontalX = (cornerX + targetX) / 2;
        const midHorizontalY = targetY;
        
        return [
          {
            x: midVerticalX,
            y: midVerticalY,
            type: 'waypoint' as const
          },
          {
            x: midHorizontalX,
            y: midHorizontalY,
            type: 'waypoint' as const
          }
        ];
      } else {
        // For straight lines, create waypoint in the middle
        const midX = (sourceX + targetX) / 2;
        const midY = (sourceY + targetY) / 2;
        
        return [{
          x: midX,
          y: midY,
          type: 'waypoint' as const
        }];
      }
    }
  }, [sourceX, sourceY, targetX, targetY]);

  const defaultWaypoints = React.useMemo(() => generateWaypoints(pathPoints, edgeType), [generateWaypoints, pathPoints, edgeType]);

  const [renderWaypoints, setRenderWaypoints] = useState<RoutePoint[]>(defaultWaypoints);

  const areWaypointsEqual = (a: RoutePoint[], b: RoutePoint[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i].x !== b[i].x || a[i].y !== b[i].y || a[i].type !== b[i].type) return false;
    }
    return true;
  };

  // Sync from props when not dragging, but only if changed
  React.useEffect(() => {
    if (!isDragging && !areWaypointsEqual(renderWaypoints, defaultWaypoints)) {
      setRenderWaypoints(defaultWaypoints);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultWaypoints, isDragging]);
  // Track which index is being dragged to avoid equality issues by coords
  const draggingIndexRef = React.useRef<number | null>(null);
  
  // console.log('🔵 InteractiveWaypoints:', { isSelected, pathPointsLength: pathPoints.length, waypointsLength: waypoints.length, edgeType });

  const handleWaypointPointerDown = useCallback((event: React.PointerEvent<SVGCircleElement>, waypoint: RoutePoint, index: number) => {
    event.stopPropagation();
    event.preventDefault();
    // Set pointer capture so subsequent pointer events continue even if cursor leaves the element
    try {
      (event.currentTarget as unknown as SVGGraphicsElement).setPointerCapture(event.pointerId);
      pointerTargetRef.current = event.currentTarget as unknown as SVGElement;
      pointerIdRef.current = event.pointerId;
    } catch (e) {
      console.warn('setPointerCapture failed:', e);
    }
    setIsDragging(true);
    dragRef.current = waypoint;
    dragIndexRef.current = index;
    draggingIndexRef.current = index;
    setDragPosition({ x: waypoint.x, y: waypoint.y });
    // Use waypoint center as drag offset
    setDragOffset({ x: 6, y: 6 });
  }, []);

  const handlePointerMove = useCallback((event: React.PointerEvent<SVGCircleElement>) => {
    if (!isDragging || dragRef.current == null || dragIndexRef.current == null) return;
    const { x, y } = screenToFlowPosition({
      x: event.clientX - dragOffset.x,
      y: event.clientY - dragOffset.y
    });
    let constrainedX = x;
    let constrainedY = y;
    if (edgeType === 'smoothstep' || edgeType === 'step') {
      if (dragIndexRef.current === 0) {
        constrainedX = x; // vertical segment midpoint → move along X only
        constrainedY = dragRef.current.y;
      } else if (dragIndexRef.current === 1) {
        constrainedX = dragRef.current.x;
        constrainedY = y; // horizontal segment midpoint → move along Y only
      }
    }
    setDragPosition({ x: constrainedX, y: constrainedY });
    const updatedWaypoints = renderWaypoints.map((wp, idx) =>
      idx === dragIndexRef.current ? { ...wp, x: constrainedX, y: constrainedY } : wp
    );
    setRenderWaypoints(updatedWaypoints);
    // Throttle parent updates via rAF to reduce re-render spam
    pendingWaypointsRef.current = updatedWaypoints;
    if (rafIdRef.current == null) {
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        if (pendingWaypointsRef.current) {
          onWaypointUpdate(pendingWaypointsRef.current);
          pendingWaypointsRef.current = null;
        }
      });
    }
    // (optional throttled log removed)
  }, [isDragging, dragOffset, screenToFlowPosition, edgeType, onWaypointUpdate, renderWaypoints]);

  const handlePointerUp = useCallback((event: React.PointerEvent<SVGCircleElement>) => {
    if (!isDragging) return;
    event.stopPropagation();
    event.preventDefault();
    // Release pointer capture
    try {
      if (event.currentTarget && pointerIdRef.current != null) {
        (event.currentTarget as unknown as SVGGraphicsElement).releasePointerCapture(pointerIdRef.current);
      }
    } catch (e) {
      console.warn('releasePointerCapture failed:', e);
    }
    // Flush any pending update to parent
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (pendingWaypointsRef.current) {
      onWaypointUpdate(pendingWaypointsRef.current);
      pendingWaypointsRef.current = null;
    }
    setIsDragging(false);
    dragRef.current = null;
    dragIndexRef.current = null;
    draggingIndexRef.current = null;
    pointerIdRef.current = null;
    pointerTargetRef.current = null;
  }, [isDragging]);

  // No global listeners; rely on pointer capture on the circle itself

  // Render guards AFTER all hooks to keep hook order stable
  if (!isSelected || renderWaypoints.length === 0) {
    return null;
  }

  // console.log('🔵 WAYPOINTS COMPONENT LOADED! Circles:', waypoints.length);
  
  return (
    <g className="interactive-waypoints" style={{ pointerEvents: 'all', zIndex: 1000 }}>
      {renderWaypoints.map((waypoint, index) => {
        // Use drag position if this waypoint is being dragged (by index)
        const isBeingDragged = isDragging && draggingIndexRef.current === index;
        const x = isBeingDragged ? dragPosition.x : waypoint.x;
        const y = isBeingDragged ? dragPosition.y : waypoint.y;
        
        return (
          <g key={`waypoint-${index}`} style={{ pointerEvents: 'none' }}>
            {/* Blue circle - interactive handle */}
            <circle
              cx={x}
              cy={y}
              r="8"
              fill="#3b82f6"
              stroke="#ffffff"
              strokeWidth="2"
              style={{ pointerEvents: 'all', cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
              onPointerDown={(e) => handleWaypointPointerDown(e, waypoint, index)}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onLostPointerCapture={handlePointerUp}
            />
            {/* Inner white dot (visual only) */}
            <circle
              cx={x}
              cy={y}
              r="2"
              fill="#ffffff"
              style={{ pointerEvents: 'none' }}
            />
          </g>
        );
      })}
    </g>
  );
};

export default InteractiveWaypoints;
